import React from 'react';
import { Button, Card, Badge, Spinner } from 'flowbite-react';
import { useGetMyTickets } from 'src/hooks/support.hooks';
import { SupportTicketStatus } from 'types';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { HiChatAlt2 } from 'react-icons/hi';

type TabValue = 'unresolved' | 'resolved' | 'all';

const formatTicketDate = (date: string) =>
	new Date(date).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });

const labelToSlug = (label: string) => (label || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const MyTicketsSection = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const tabParam = searchParams.get('tab') || 'unresolved';
	const tab: TabValue = tabParam === 'resolved' || tabParam === 'all' ? tabParam : 'unresolved';

	const statusFilter = tab === 'resolved' ? SupportTicketStatus.RESOLVED : tab === 'unresolved' ? SupportTicketStatus.OPEN : '';

	const { data: ticketsData, isLoading } = useGetMyTickets(statusFilter);

	const tickets = ticketsData?.data || [];

	const getStatusColor = (status: SupportTicketStatus | string) => {
		const colors: Record<SupportTicketStatus, string> = {
			[SupportTicketStatus.OPEN]: 'info',
			[SupportTicketStatus.RESOLVED]: 'success',
		};
		return colors[status as SupportTicketStatus] ?? (status === 'in_progress' || status === 'escalated' ? 'info' : status === 'closed' ? 'success' : 'gray');
	};

	const getStatusLabel = (status: string) => (status === 'in_progress' || status === 'escalated' ? 'Open' : status === 'closed' ? 'Resolved' : (status?.charAt(0)?.toUpperCase() ?? '') + (status?.slice(1) ?? ''));

	const setTab = (newTab: TabValue) => {
		const next = new URLSearchParams(searchParams);
		next.set('tickets', 'ticketlisting');
		next.set('tab', newTab);
		navigate(`/support?${next.toString()}`, { replace: true });
	};

	const openTicketInModal = (ticket: any) => {
		const next = new URLSearchParams(searchParams);
		next.set('tickets', 'ticketlisting');
		if (!next.has('tab')) next.set('tab', 'unresolved');
		next.set('step', 'ticketdetails');
		next.set('ticketId', ticket._id);
		const catLabel = typeof ticket.category === 'object' ? (ticket.category?.label || ticket.category?.name) : typeof ticket.category === 'string' ? ticket.category : '';
		const subLabel = typeof ticket.subcategory === 'object' ? (ticket.subcategory?.label || ticket.subcategory?.name) : typeof ticket.subcategory === 'string' ? ticket.subcategory : '';
		if (catLabel) next.set('category', labelToSlug(catLabel));
		if (subLabel) next.set('subcategory', labelToSlug(subLabel));
		navigate(`/support?${next.toString()}`, { replace: true });
	};

	return (
		<Card className="bg-white dark:bg-[#1F2A37]">
			<div className="flex md:flex-row flex-col justify-between items-center mb-4 md:gap-0 gap-2">
				<h2 className="text-xl font-semibold font-inter dark:text-white">My Support Tickets</h2>
				<div className="flex gap-2">
					<Button
						size="sm"
						color={tab === 'unresolved' ? 'purplePlaced' : 'gray'}
						onClick={() => setTab('unresolved')}
					>
						Unresolved
					</Button>
					<Button
						size="sm"
						color={tab === 'resolved' ? 'purplePlaced' : 'gray'}
						onClick={() => setTab('resolved')}
					>
						Resolved
					</Button>
					<Button
						size="sm"
						color={tab === 'all' ? 'purplePlaced' : 'gray'}
						onClick={() => setTab('all')}
					>
						All
					</Button>
				</div>
			</div>

			{isLoading ? (
				<div className="flex justify-center py-8">
					<Spinner size="xl" />
				</div>
			) : tickets.length === 0 ? (
				<div className="text-center py-8 text-gray-500 dark:text-gray-400">
					No tickets found. Create a ticket by selecting a question above.
				</div>
			) : (
				<div className="space-y-3">
					{tickets.map((ticket: any) => {
						const categoryLabel = typeof ticket.category === 'object'
							? (ticket.category?.label || ticket.category?.name)
							: typeof ticket.category === 'string' ? ticket.category : '';
						const subcategoryLabel = typeof ticket.subcategory === 'object'
							? (ticket.subcategory?.label || ticket.subcategory?.name)
							: typeof ticket.subcategory === 'string' ? ticket.subcategory : '';
						const categoryDisplay = categoryLabel || subcategoryLabel || 'Support';
						return (
							<button
								key={ticket._id}
								type="button"
								onClick={() => openTicketInModal(ticket)}
								className="w-full text-left p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center justify-between gap-4"
							>
								<div className="min-w-0 flex-1">
									<p className="font-medium text-gray-900 dark:text-white truncate">{ticket.subject}</p>
									<div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500 dark:text-gray-400">
										<HiChatAlt2 className="w-4 h-4 flex-shrink-0" />
										<span className="truncate">
											{categoryDisplay}
											{categoryDisplay && ' • '}
											{formatTicketDate(ticket.createdAt)}
										</span>
									</div>
								</div>
								<Badge color={getStatusColor(ticket.status)} className="flex-shrink-0 capitalize">
									{getStatusLabel(ticket.status)}
								</Badge>
							</button>
						);
					})}
				</div>
			)}
		</Card>
	);
};

export default MyTicketsSection;


